import React from 'react';
import { FormattedMessage } from 'react-intl';
import { isEqual } from 'lodash';

import {
  Checkbox,
  CircularProgress,
  IconButton,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  HeatPump,
  PushPin,
  RadioButtonChecked,
  RadioButtonUnchecked,
  SolarPower,
  Wifi,
  WifiOff,
} from '@mui/icons-material';

import { Row } from '../../atoms/row';
import { SimpleTable } from '../../molecules/simple-table';
import { useMakeDhcpLeaseStatic, useRemoveDhcpHost } from './network.hooks';
import { useSelector } from '../../store/utils';
import { RouterOutput } from '../../api/trpc';

type Props = {
  variant?: 'default' | 'select';
  value?: { mac: string; ip: string };
  onChange?: (value: { mac: string; ip: string } | undefined) => void;
  paperSx?: SxProps;
  getData: {
    data?: RouterOutput['network']['get'];
    isLoading: boolean;
    isError: boolean;
  };
};

export const NetworkHostsTable = ({
  variant = 'default',
  paperSx,
  value,
  onChange,
  getData: { data, isLoading, isError },
}: Props) => {
  const [managedNetwork, inverterAddress, inverterConfig, heatPumpAddress] =
    useSelector(
      (s) =>
        [
          s.variables.NETWORK_CAPABILITIES.managedNetwork,
          s.variables.MODBUS_TEST_ADDRESS,
          s.variables.INVERTER_CONFIG,
          s.variables.HP_ECOONE_API_IP,
        ] as const
    );

  const { onDhcpHostRemove } = useRemoveDhcpHost();
  const { onMakeDhcpLeaseStatic } = useMakeDhcpLeaseStatic();

  if (!managedNetwork) return null;

  if (isLoading) {
    return (
      <CircularProgress sx={{ mx: 'auto', display: 'block', mt: 2, mb: 2 }} />
    );
  }

  if (isError) {
    return (
      <Typography
        variant="h3"
        sx={{ textAlign: 'center', my: 2, color: 'error.main' }}
      >
        <FormattedMessage
          id="network.summary.error"
          defaultMessage="Chyba při načítání"
        />
      </Typography>
    );
  }

  if (!data?.summary) {
    return null;
  }

  return (
    <SimpleTable
      rows={[
        [
          <FormattedMessage
            id="network.summary.name"
            defaultMessage="Jméno"
            key={0}
          />,
          <FormattedMessage
            id="network.summary.mac"
            defaultMessage="MAC"
            key={0}
          />,
          <FormattedMessage
            id="network.summary.address"
            defaultMessage="IP adresa"
            key={0}
          />,
          '',
        ],
        ...Object.entries(data.summary).map(([mac, item]) => {
          if (!item) return [];

          const { dhcpHost, dhcpLease, wifiConnected, neighbor } = item;

          const address =
            dhcpHost?.address ?? dhcpLease?.ip ?? neighbor?.address;

          const isInverter =
            inverterAddress === address ||
            Object.values(inverterConfig).some(
              (inverter) =>
                'address' in inverter && inverter.address === address
            );
          const isHeatPump = heatPumpAddress === address;

          return [
            <Row key={0} gap={0.25}>
              <span>{(dhcpHost?.name ?? dhcpLease?.name) || '---'}</span>
              {wifiConnected ? (
                <Tooltip
                  title={`WiFi: ${wifiConnected.txBitrate} - ${wifiConnected.rxBitrate}`}
                >
                  <IconButton size="small">
                    <Wifi />
                  </IconButton>
                </Tooltip>
              ) : dhcpLease ? null : (
                <Tooltip
                  title={
                    <FormattedMessage
                      id="network.summary.not_connected"
                      defaultMessage="Není připojeno"
                    />
                  }
                >
                  <IconButton size="small">
                    <WifiOff />
                  </IconButton>
                </Tooltip>
              )}

              {isInverter && (
                <Tooltip
                  title={
                    <FormattedMessage
                      id="network.summary.inverter"
                      defaultMessage="Střídač"
                    />
                  }
                >
                  <IconButton size="small">
                    <SolarPower />
                  </IconButton>
                </Tooltip>
              )}

              {isHeatPump && (
                <Tooltip
                  title={
                    <FormattedMessage
                      id="network.summary.heat_pump"
                      defaultMessage="Tepelné čerpadlo"
                    />
                  }
                >
                  <IconButton size="small">
                    <HeatPump />
                  </IconButton>
                </Tooltip>
              )}
            </Row>,
            mac,
            <Row key={2} gap={0.25}>
              <span>{address}</span>

              {/* default - manual host control */}
              {variant === 'default' && (
                <>
                  {dhcpHost ? (
                    <Tooltip
                      title={
                        <FormattedMessage
                          id="network.summary.dhcp_host_exists"
                          defaultMessage="Adresa zafixována na DHCP. Kliknutím odfixovat."
                        />
                      }
                      onClick={() => onDhcpHostRemove(dhcpHost.index)}
                    >
                      <IconButton size="small">
                        <PushPin sx={{ color: 'success.main' }} />
                      </IconButton>
                    </Tooltip>
                  ) : dhcpLease ? (
                    <Tooltip
                      title={
                        <FormattedMessage
                          id="network.summary.no_dhcp_host"
                          defaultMessage="Dynamická adresa na DHCP. Kliknutím zafixovat."
                        />
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          onMakeDhcpLeaseStatic({
                            address: dhcpLease.ip,
                            mac: dhcpLease.mac,
                            name: dhcpLease.name,
                          })
                        }
                      >
                        <PushPin />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </>
              )}
            </Row>,
            <Row key={3}>
              {variant === 'select' && (
                <Checkbox
                  icon={<RadioButtonUnchecked />}
                  checkedIcon={<RadioButtonChecked />}
                  checked={isEqual({ mac, ip: address }, value)}
                  onChange={() => {
                    if (onChange && address) onChange({ mac, ip: address });
                  }}
                />
              )}
            </Row>,
          ];
        }),
      ]}
      firstLineBold
      lastColBold={false}
      sx={paperSx}
    />
  );
};
